import React, {useState} from "react"
import Recipients from "@irati-web/reward/src/components/Recipients"
import authentication from "@irati/auth"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2, H3, Body} from "@irati/ui/src/Typography"
import GenericModal from "@irati/ui/src/GenericModal"
import history from "@irati/utils/history"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import Spacing from "@irati/ui/src/Spacing"
import {CelebrationTag, TeamAssignment, User} from "@irati/types"

import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import rewardEmployees from "@irati/ui/images/reward-employees.svg"
import assignBudget from "@irati/ui/images/assign-budget.svg"
import ErrorText from "@irati/ui/src/ErrorText"
import {DistributeRewardImportType, DistributeRewardRecipient} from "../../../enums/DistributeRewardTypes"
import {useRewardsDistribution, useRewardsDistributionActions} from "../../../stores/DistributeReward/hooks"
import ButtonTabs from "../ButtonTabs"
import TemplatesCarousel from "../../TemplatesCarousel"
import DistributeRewardStepIndicator from "./DistributeRewardStepIndicator"
import UploadCSVContent from "./UploadCSVContent"
import UploadCsvButtonTab from "./UploadCsvButtonTab"
import AllocationJourneyCardOption from "./AllocationJourneyCardOption"
import {CTA} from "./styled"
import {LARGE_DESKTOP, PHONE, PHONE_LANDSCAPE, TABLET} from "@irati/ui/src/global/media"

interface Props extends InjectIntl {}

const options = [
  {
    title: "wallet.reward.admin.option.reward",
    img: rewardEmployees,
    recipientType: DistributeRewardRecipient.EMPLOYEE
  },
  {
    title: "wallet.reward.admin.option.assign",
    img: assignBudget,
    recipientType: DistributeRewardRecipient.MANAGER
  }
]
const templatesSliderSettings = {
  slidesToScroll: 2,
  slidesToShow: 3,
  responsive: [
    {breakpoint: PHONE - 1, settings: {slidesToShow: 1, slidesToScroll: 1}},
    {breakpoint: PHONE_LANDSCAPE - 1, settings: {slidesToShow: 1.5, slidesToScroll: 1}},
    {breakpoint: TABLET - 1, settings: {slidesToShow: 2.5, slidesToScroll: 1}},
    {breakpoint: LARGE_DESKTOP - 1, settings: {slidesToShow: 2.5, slidesToScroll: 2}}
  ]
}
const DistributeRewardStep1: React.FC<Props> = ({formatMessage}) => {
  const payload = useRewardsDistribution()
  const {dispatch} = useRewardsDistributionActions()
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false)
  const isAdmin = authentication.apps("wallet.flexiRewards.canTopUpRewardPoints")
  const toggleSuccessModal = (show: boolean) => setShowSuccessModal(show)

  const editManagers = () => {
    history.push("/admin/teams/view")
  }

  const handleRecipients = (beneficiary: User[] | string[]) => {
    let recipients = beneficiary
    if (payload.recipientType === DistributeRewardRecipient.MANAGER) {
      recipients = (beneficiary as User[]).filter((user) => {
        const isManagerOrLeader = user?.teamAssignments?.find((teamAssignment) => teamAssignment.assignment !== TeamAssignment.Basic)
        if (!isManagerOrLeader) {
          setShowSuccessModal(true)
        }
        return isManagerOrLeader
      })
    } else {
      dispatch({
        type: "SET_RECOGNITION",
        payload: {
          recognition: {userIds: beneficiary as string[]}
        }
      })
    }
    dispatch({
      type: "UPDATE_RECIPIENTS",
      payload: {
        recipientsList: recipients
      }
    })
  }

  const handleSwitchUploadMethod = (id: string) => {
    const rewardImportType = id as unknown as DistributeRewardImportType
    if (rewardImportType === DistributeRewardImportType.MANUAL_ENTER) {
      tracking.employeesManualEnter()
    } else {
      tracking.employeesCSVUpload()
    }
    dispatch({
      type: "SET_RECOGNITION",
      payload: {
        recognition: {userIds: []}
      }
    })
    dispatch({
      type: "SWITCH_IMPORT_TYPE",
      payload: {
        importType: rewardImportType
      }
    })
  }

  const handleOptionClick = (option: typeof options[number]) => {
    dispatch({type: "SWITCH_RECIPIENT", payload: {recipientType: option.recipientType}})
    if (option.recipientType === DistributeRewardRecipient.EMPLOYEE) {
      tracking.selectRewardEmployees(isAdmin ? "Admin" : "Manager")
    } else {
      tracking.selectAssignBudget(isAdmin ? "Admin" : "Manager")
    }
  }
  const onTagSelected = (tag: CelebrationTag) => {
    dispatch({
      type: "SET_TEMPLATE",
      payload: {
        tag
      }
    })
    dispatch({
      type: "SET_REWARD_TEMPLATE_TITLE",
      payload: {
        title: tag?.label
      }
    })
  }
  return (
    <>
      <DistributeRewardStepIndicator currentStep={1} />
      <H3 marginTop={2} marginBottom={2}>
        <FormattedMessage id="wallet.reward.admin.distribute.recipientsStepTitle" />
      </H3>

      <>
        <Flex wrap="wrap" justifyContent="space-between" gap={2}>
          {options.map((option) => (
            <AllocationJourneyCardOption
              key={option.title}
              title={option.title}
              img={option.img}
              selected={option.recipientType === payload.recipientType}
              onClick={() => handleOptionClick(option)}
            />
          ))}
        </Flex>
        {payload.errors?.noRecipientType && (
          <Spacing marginTop={2}>
            <ErrorText>
              <FormattedMessage id="platform.options.select" />
            </ErrorText>
          </Spacing>
        )}
      </>

      {payload.recipientType === DistributeRewardRecipient.EMPLOYEE && (
        <>
          <Flex marginTop={6} marginBottom={6} direction="column">
            <Body large>
              <FormattedMessage id="wallet.templates.chooseTemplate.header" />
            </Body>
            <Flex gap={1} alignItems="center">
              <Body small>
                <FormattedMessage id="wallet.templates.chooseTemplate.subtitle" />
              </Body>
              <CTA cta small href={formatMessage({id: "wallet.reward.templates.learnMoreLink"})} target="_blank">
                <FormattedMessage id="wallet.reward.templates.learnMore" />
              </CTA>
            </Flex>
            <TemplatesCarousel
              onChange={onTagSelected}
              defaultValue={payload.rewardTemplate?.uuid}
              sliderSettings={templatesSliderSettings}
            />
          </Flex>

          <H3 marginTop={2} marginBottom={2}>
            <FormattedMessage id="wallet.reward.admin.distribute.recipientsStepSecondaryTitle" />
          </H3>
          <ButtonTabs
            buttons={[
              {id: DistributeRewardImportType.MANUAL_ENTER, title: <FormattedMessage id="wallet.adminReward.manualEnter" />},
              {id: DistributeRewardImportType.IMPORT_CSV, title: <UploadCsvButtonTab formatMessage={formatMessage} />}
            ]}
            selected={payload.rewardImportType}
            onClick={handleSwitchUploadMethod}
          />

          {payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER && (
            <Recipients
              recipients={payload.destinationUsers}
              setRecipients={handleRecipients}
              sameBUOnly={false}
              isRequired={!payload.destinationUsers.length}
            />
          )}
          {payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV && (
            <UploadCSVContent setRecipients={handleRecipients} payload={payload} dispatch={dispatch} />
          )}
        </>
      )}
      {payload.recipientType === DistributeRewardRecipient.MANAGER && (
        <>
          <H3 marginTop={2} marginBottom={2}>
            <FormattedMessage id="wallet.reward.admin.distribute.recipientsStepSecondaryTitle" />
          </H3>
          <Recipients
            recipients={payload.destinationUsers}
            setRecipients={handleRecipients}
            sameBUOnly={false}
            isRequired={!payload.destinationUsers.length}
          />
        </>
      )}

      <GenericModal hideActions show={showSuccessModal} onHide={() => toggleSuccessModal(false)}>
        <H2 marginBottom={1}>
          <FormattedMessage id="wallet.reward.modal.error.heading.oops" />
        </H2>
        <Body>
          <FormattedMessage id="wallet.reward.modal.body.text" />
        </Body>
        <Flex marginTop={5}>
          <Button id="rewardWallet_distributeReward_editManagers_button" primary marginRight onClick={editManagers}>
            <FormattedMessage id="wallet.reward.modal.editManagers" />
          </Button>
          <Button id="rewardWallet_distributeReward_maybeLater_button" onClick={() => toggleSuccessModal(false)}>
            <FormattedMessage id="wallet.reward.modal.maybeLater" />
          </Button>
        </Flex>
      </GenericModal>
    </>
  )
}

export default injectIntl(DistributeRewardStep1)
